'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import Text from '../text'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { HeroBannerData } from '@/lib/api/strapi/herro-banner/hero-banner.types'
import TextPageContent from '../text-page-content'

interface InteractiveCarouselProps {
  slides: HeroBannerData[]
}

const InteractiveCarousel = ({ slides }: InteractiveCarouselProps) => {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!api) {
      return
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap() + 1)

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])

  const handleClick = (i: number) => {
    if (api) {
      api.scrollTo(i)
      setCurrent(i + 1)
    }
  }

  return (
    <Carousel setApi={setApi}>
      <CarouselContent className="md:aspect-[1440/841]">
        {slides.map((slide) => (
          <CarouselItem key={slide.id} className="flex flex-col md:flex-row">
            <Image
              width={726}
              height={841}
              src={slide.attributes?.image.data.attributes.url}
              alt={
                slide.attributes?.image.data.attributes.alternativeText ||
                'banner'
              }
              className="aspect-square w-full flex-1 object-cover sm:aspect-[4/3] md:aspect-[3/4]"
              priority
            />

            <div className="flex flex-1 items-center">
              <div className="mx-5 mb-8 mt-10.5 sm:mx-30 sm:my-25 md:my-9 md:max-w-md">
                <Text as="h1" uppercase>
                  {slide.attributes.banner_title.title}
                </Text>
                <Text
                  as="h5"
                  className="mb-4 tracking-wide sm:tracking-widest"
                  uppercase
                >
                  {slide.attributes.banner_title.subtitle}
                </Text>
                <TextPageContent
                  className="max-w-[calc(352/16*1rem)] sm:max-w-[calc(368/16*1rem)]"
                  content={slide.attributes.content}
                />
                <Button asChild className="mt-12 uppercase">
                  <Link href={slide.attributes.cta.href}>
                    {slide.attributes.cta.label}
                  </Link>
                </Button>
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className="pointer-events-none absolute top-6 flex aspect-square w-full items-end gap-2 sm:bottom-3 sm:aspect-auto md:px-20">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            onClick={() => handleClick(i)}
            className="pointer-events-auto flex-1 py-2"
          >
            <div
              className={cn(
                'h-1',
                i === current - 1 ? 'bg-primary-400' : 'bg-primary-100',
              )}
            ></div>
          </button>
        ))}
      </div>
    </Carousel>
  )
}

export default InteractiveCarousel
